import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fuelAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Fuel, ChevronRight, X } from 'lucide-react';
import { Button } from '../components/ui/button';

const FuelCaptureBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const envPrefix = location.pathname.startsWith('/test') ? '/test' : '';
  const { isSupervisor, isAdmin } = useAuth();
  const [fuelStats, setFuelStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const fetchFuelStats = useCallback(async () => {
    if (!isSupervisor && !isAdmin) return;
    
    try {
      const response = await fuelAPI.getStatistics();
      setFuelStats(response.data);
    } catch (error) {
      console.error('Failed to fetch fuel stats:', error);
    } finally {
      setLoading(false);
    }
  }, [isSupervisor, isAdmin]);

  useEffect(() => {
    fetchFuelStats();
    // Refresh status every 5 minutes
    const interval = setInterval(fetchFuelStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchFuelStats]);

  // Don't show for non-supervisors
  if (!isSupervisor && !isAdmin) return null;
  if (loading) return null;
  if (dismissed) return null;

  return (
    <div 
      className="bg-amber-50 border-b-2 border-amber-400 px-4 py-3"
      data-testid="fuel-capture-banner"
    >
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-amber-500 p-2 rounded-lg">
            <Fuel className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-semibold text-amber-800">
              Daily Fuel Slip Capture
            </p>
            <p className="text-sm text-amber-700">
              Ensure all fuel slips for today have been captured and uploaded
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button 
            onClick={() => navigate(`${envPrefix}/fuel-schedule`)}
            className="bg-amber-600 hover:bg-amber-700 text-white flex-1 sm:flex-none"
            data-testid="fuel-capture-btn"
          >
            Capture Fuel Slip
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setDismissed(true)}
            className="text-amber-700 hover:bg-amber-100"
            title="Dismiss for now"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FuelCaptureBanner;
